/**
 * 이상한 방 - 매치 세션 뷰 (`ClientView` → 화면 모듈이 읽을 모양)
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 11.2절(클라이언트가 가진 것) · 11.3절(서버가 쥐는 것)
 *            로드맵 M2(`matchSessionView`는 `ClientView.self → GameHudView` 이름 옮기기만 - 산술 0)
 *
 * `sessionView.ts`의 여럿이 판이다. 여기 있는 것은 전부 **인자만 보는 순수 함수**이고, 하는 일은 호스트가 보낸
 * 투영(`ClientView`)을 HUD·결과 화면·개발 훅이 아는 모양으로 옮기는 **이름 옮기기** 하나뿐이다.
 *
 * ## 산술이 없다 - 소스 스캔이 지킨다
 *
 * 뷰의 불리언(`canShoot` · `canUseMagazine` · `isLowAmmo`)과 수치(배수 · 상한 · 잔여 시간)는 호스트가 리듀서의
 * 게이트를 **그대로 호출**해 내린 값이다(`matchView.ts` 머리말). 여기서 하나라도 다시 계산하면 그 규칙이 클라이언트에
 * 두 벌째로 생기고, 호스트와 화면이 갈리는 순간 계기판은 "눌리는데 아무 일도 없는" 물건이 된다.
 * 그래서 이 파일에는 `+ - * /`가 **한 줄도 없다**(`matchSessionView.test.ts`가 주석·문자열을 벗긴 소스에서 검사한다).
 * 시간 보간(`step.atMs` 이후 흐른 만큼 빼기)은 산술이므로 `matchClock.ts`가 하고, 여기는 그 결과를 받아 옮긴다.
 *
 * ## `ui/`의 타입을 가져오는 이유
 *
 * 세션은 접착층이라 위 레이어를 전부 부른다(CLAUDE.md의 의도된 예외). 이 파일은 그 세션의 한 조각이므로 같은 예외
 * 안에 있고, 실제로 가져오는 것은 **뷰 타입뿐**이다(`import type`). `boundary.test.ts`가 `session*`과 함께
 * `matchSession*`을 금지 목록에 두므로 매치 리듀서·네트 층이 이 파일을 부를 길은 없다.
 */

import type { PlayerId } from '../core/types'
import type { ZoneProgressView } from '../ui/zoneProgress'
import type { DoorQueueView } from '../ui/doorQueue'
import type { GameHudView } from '../ui/gameHud'
import type { PlayerStripRow, PlayerStripView } from '../ui/playerStrip'
import type { MissedChangeView, ResultRankingRow, ResultView, RoundEndReason } from '../ui/resultScreen'
import type { SpectateBarView } from '../ui/spectateBar'
import type { ClientView, DoorView, PublicPlayer } from './match/matchView'
import type { OriginRoomStatus } from './originVisit'
import type { SessionDebugInfo } from './sessionView'

/* --- 조회 -------------------------------------------------------------------------- */

/** 좌석 하나. 없으면 `undefined` - 뷰의 `players`는 좌석 순서라 id로 찾는다. */
export function playerOf(view: ClientView, id: PlayerId): PublicPlayer | undefined {
  return view.players.find((player) => player.id === id)
}

/** 화면에 찍을 이름. 좌석이 사라졌으면(로비에서 나갔다) id를 그대로 - 빈 이름표보다 낫다. */
export function nameOf(view: ClientView, id: PlayerId): string {
  return playerOf(view, id)?.name ?? id
}

/**
 * 내가 원래 방 안에 있는가. `SelfView`에는 이 불리언이 없고(`origin`의 얼굴만 있다) `PublicPlayer.inOrigin`이 원본이다 -
 * 여기서 `origin.kind === 'staying'`으로 유도하면 얼굴(표시)에서 상태(규칙)를 거꾸로 읽는 것이다.
 */
export function isMeInOrigin(view: ClientView): boolean {
  return playerOf(view, view.me)?.inOrigin ?? false
}

/* --- HUD ---------------------------------------------------------------------------- */

/**
 * HUD 뷰. `SelfView`의 필드 이름이 `GameHudView`와 같게 지어진 이유가 이 함수다(`matchView.SelfView` 주석) -
 * 옮기기만 하면 된다. 시간 값 둘(`phaseRemainingMs` · `origin`)은 부르는 쪽이 `matchClock`으로 보간해 넘긴다.
 *
 * @param ammoPerMagazine 탄창 하나가 채우는 양. 뷰에 없어서(호스트가 규칙 수치를 뷰에 싣지 않는다) 튜닝에서 받는다 -
 *                        HUD가 "몇 발 더 쓰면 탄창이 들어가는가"를 말하는 데만 쓰는 값이다(`GameHudView.ammoPerMagazine`).
 */
export function buildMatchHudView(
  view: ClientView,
  phaseRemainingMs: number | null,
  origin: OriginRoomStatus,
  ammoPerMagazine: number,
): GameHudView {
  return {
    phase: view.phase,
    phaseRemainingMs,
    // "찾음 N/M"은 **내가 찾을 수 있는 것**이 분자·분모다(M11 규칙 4) - 둘 다 호스트가 센 값이고 여기서 거르지 않는다.
    foundCount: view.foundCount,
    changeCount: view.changeCount,
    skip: view.skip,
    ammo: view.self.ammo,
    ammoMax: view.self.ammoMax,
    isLowAmmo: view.self.isLowAmmo,
    magazines: view.self.magazines,
    magazineUsesLeft: view.self.magazineUsesLeft,
    canUseMagazine: view.self.canUseMagazine,
    ammoPerMagazine,
    isInOriginRoom: isMeInOrigin(view),
    origin,
    combo: view.self.combo,
    comboMultiplier: view.self.comboMultiplier,
    comboAtCap: view.self.comboAtCap,
    raised: view.self.raised,
    canRaise: view.self.canRaise,
    // 다녀간 사람·관전자에게는 걸기 칩이 없다 - 이 값은 호스트의 `isActiveHunter`(`self.hunting`) 그대로다(역할을 여기서 유도하지 않는다).
    raiseVisible: view.self.hunting,
    // 내 구획(M11). id 그대로 넘기고 이름은 HUD가 사전에서 굽는다 - 이 파일은 문구를 만들지 않는다.
    myZoneId: view.myZone?.zoneId ?? null,
  }
}

/* --- 참가자 줄 ----------------------------------------------------------------------- */

/**
 * 참가자 줄(`ui/playerStrip.ts`)의 칸 하나. `PublicPlayer`의 불리언을 그대로 옮긴다 - 관전·원래 방 안·역할은 전부 호스트가
 * 내린 값이고, "탄환이 0이면 관전"을 여기서 유도하면 규칙이 두 벌이 된다.
 *
 * 잔탄은 **내 칸에만** 있다. `PublicPlayer`에는 잔탄이 없고(남에게 보여도 되는 것이 아니다 - `matchView.ts`) `SelfView`에만 있으므로,
 * 내 좌석이면 `self.ammo`를 넣고 남이면 `null`이다. 줄은 `null`인 칸에 점을 그리지 않는다.
 */
function stripRow(view: ClientView, player: PublicPlayer): PlayerStripRow {
  const isMe = player.id === view.me
  return {
    id: player.id,
    name: player.name,
    isMe,
    /*
     * "지금 손대는 중인가"(M11). 작업대는 바꾸기 페이즈에만 있고(`matchView.deskFor`) `changeDone`은 호스트가 내린
     * 커밋 여부다 - 둘을 합치면 "아직 자기 구획을 고치는 중"이 되고, 그것이 이 표식이 말하는 것이다.
     * 역할이 사라졌으므로(M11) 라운드 내내 붙는 표식은 없다.
     */
    isVisitor: view.desk !== null && !player.changeDone,
    spectating: player.spectating,
    inOrigin: player.inOrigin,
    watched: view.self.spectateTarget === player.id,
    foundCount: player.foundCount,
    ammo: isMe ? view.self.ammo : null,
    ammoMax: view.self.ammoMax,
  }
}

/**
 * 참가자 줄이 서는 페이즈. 이 줄이 말하는 것은 **"누가 몇 개를 찾았는가"** 하나라, 아무도 찾지 않는 페이즈에서는
 * 여덟 칸이 전부 "0 찾음"이 되어 자리만 먹는다.
 *
 * 바꾸기를 뺀 이유가 그중 제일 크다. 그때는 **같은 사람들의 같은 목록이 구획 진행판으로 한 벌 더 선다**
 * (`buildZoneProgressView` - 작업대가 있는 동안만 뜬다). 폰 세로에서 그 둘이 왼쪽 열에 겹겹이 쌓여
 * 여덟 칸이 세 줄(약 150px) + 진행판 여덟 줄이 되었고, 방이 화면의 4분의 1로 줄었다
 * (원칙 2 - UI가 덮은 면적은 곧 볼 수 없는 방의 넓이). 진행판이 그 페이즈의 답을 이미 말하므로 줄은 물러난다.
 *
 * 결과를 뺀 이유는 순위표(`ui/rankingList.ts`)가 같은 여덟 명을 점수와 함께 말하기 때문이다 - 판 뒤에 남은 줄이
 * 96% 불투명한 판을 통해 순위표 위로 비쳤다(브라우저 실측 10,685px²).
 *
 * 로비·꾸미기에는 아직 찾을 것이 시작되지 않았다.
 */
const STRIP_PHASES: readonly ClientView['phase'][] = ['watch', 'hunt', 'reveal']

/**
 * 참가자 줄 뷰. 순서는 좌석 순서(`view.players`) 그대로다 - 체인저 로테이션이 그 순서라 사람이 익숙해질 수 있다.
 * 설 자리가 없는 페이즈에는 `null`이고, 그때 줄은 접힌다(`ui/playerStrip.ts` - 진행판과 같은 규약).
 */
export function buildPlayerStripView(view: ClientView): PlayerStripView | null {
  if (!STRIP_PHASES.includes(view.phase)) return null
  return { rows: view.players.map((player) => stripRow(view, player)) }
}

/* --- 시점 줄 ------------------------------------------------------------------------- */

/**
 * 시점 줄(`ui/spectateBar.ts`) 뷰. 버튼이 서는 사람은 호스트가 `spectatable`로 내린 좌석만이다(M5 규칙 2 - `matchShoot.canSpectate`
 * 그대로). 여기서 `spectating`·`role`을 읽어 목록을 만들면 그 규칙이 클라이언트에 두 벌째로 생긴다 - 하는 일은 걸러 옮기기뿐이다.
 * 관전이 아니면 `spectatable`이 전부 거짓이라 목록이 비고, 줄은 `spectating`으로 숨는다.
 */
export function buildSpectateBarView(view: ClientView): SpectateBarView {
  return {
    spectating: view.self.spectating,
    targets: view.players
      .filter((player) => player.spectatable)
      .map((player) => ({ id: player.id, name: player.name, watched: view.self.spectateTarget === player.id })),
  }
}

/* --- 문 앞 줄 ------------------------------------------------------------------------- */

/**
 * 문 앞 줄(`ui/doorQueue.ts`) 뷰. 시간 둘(보유자의 남은 체류 · 예약 유예 잔여)은 **`door` 뷰에서만** 온다(M5 규칙 5) - 부르는 쪽이
 * `matchClock.doorAfter`로 보간해 넘기고, 여기는 그 ms를 이름과 함께 옮긴다. 보유자·예약자의 id → 이름은 여기서 찾는다(화면에 id가
 * 나올 일은 없다). 내 줄 자리는 호스트가 센 `self.queuePosition` 그대로다.
 *
 * 게이지의 분모(`reserveTotalMs`)도 문 뷰의 것이다 - 분자와 같은 출처여야 서버(M6)에서 호스트·클라이언트의 `?reserve=`가
 * 갈린 날 비율이 거짓이 되지 않는다(한때 세션이 자기 튜닝에서 만들었다).
 *
 * @param door 보간이 끝난 문 뷰(`doorAfter(view.door, since)`).
 */
export function buildDoorQueueView(view: ClientView, door: DoorView): DoorQueueView {
  return {
    holderName: door.holder === null ? null : nameOf(view, door.holder),
    holderIsMe: door.holder === view.me,
    holderRemainingMs: door.holderRemainingMs,
    queue: door.queue,
    myQueuePosition: view.self.queuePosition,
    reservedName: door.reservedFor === null ? null : nameOf(view, door.reservedFor),
    reservedIsMe: door.reservedFor === view.me,
    reservedRemainingMs: door.reservedRemainingMs,
    reserveTotalMs: door.reserveTotalMs,
    aside: view.self.spectating,
  }
}

/* --- 구획 진행 ------------------------------------------------------------------------- */

/**
 * 진행판(`ui/zoneProgress.ts`) 뷰. **바꾸기 중에만** 있고 그 밖에는 `null`이다 - "바꾸기인가"의 근거는 작업대의 존재
 * (`desk`는 바꾸기 페이즈에만 온다, `matchView.deskFor`)이고 여기서 `phase`를 다시 보지 않는다.
 *
 * 줄은 **나를 뺀 좌석 전부**다(M11 - 전원이 동시에 자기 구획을 고친다). 내 진행은 내 띠(`ui/changerBar.ts`)의 점이
 * 이미 그리고 있어 같은 값을 두 자리에 두면 한 틱 차이로 어긋난다 - 시계를 두 곳에 그리지 않는 것과 같은 결정이다.
 *
 * 옮기는 것은 셋뿐이다 - 이름 · 그 사람이 만든 개수(`PublicPlayer.changeUsed`) · 상한(`desk.budget`).
 * 상한이 **내** 작업대의 것인 이유는 인당 예산이 전원 같은 값이기 때문이다(`matchDesk.freshDesks` - 방 설정 하나에서
 * 나온다). 남의 예산이 뷰에 따로 없는 것도 그래서다. 무엇을 건드렸는지는 진행판의 모양(`ZoneProgressRow`)에
 * **자리 자체가 없다** - 필드가 없으면 그릴 수도 없고, 그것이 곧 "남의 초안은 새지 않는다"의 증명이다
 * (`matchSessionView.test.ts`가 JSON으로 검사한다). 다 했는가는 호스트가 리듀서의 마감 판정(`matchDesk.deskDone`)으로
 * 내린 `changeDone` 그대로다 - 화면이 리듀서보다 오래 기다리지 않는다.
 */
export function buildZoneProgressView(view: ClientView): ZoneProgressView | null {
  const desk = view.desk
  if (desk === null) return null
  return {
    rows: view.players
      .filter((player) => player.id !== view.me)
      .map((player) => ({
        id: player.id,
        name: player.name,
        used: player.changeUsed,
        budget: desk.budget,
        done: player.changeDone,
      })),
  }
}

/* --- 개발 훅 ------------------------------------------------------------------------- */

/** 아직 뷰가 없을 때(`welcome` 전)의 개발 훅 답. 지어내지 않는다 - 숫자는 전부 0이고 페이즈가 그 사실을 말한다. */
const CONNECTING_INFO: SessionDebugInfo = {
  kind: 'match',
  phase: 'connecting',
  seed: '',
  attempt: null,
  changeSeed: null,
  ammo: 0,
  ammoMax: 0,
  magazines: 0,
  magazinesUsed: null,
  found: 0,
  changes: 0,
  isInOriginRoom: false,
  score: 0,
}

/**
 * `__weirdRoom.stats()`가 읽는 모양. 싱글(`sessionView.buildDebugInfo`)과 같은 타입이다.
 * 여럿이에는 시도 번호가 없다(라운드 변화 시드는 `${seed}:r${round}` - `matchPhases.roundChangeSeed`). 그 둘과
 * `magazinesUsed`(뷰에 없다)는 `null`.
 * `found`는 **방 전체**의 발견 수다(공유 목록) - 내 것만 세려면 `players`의 `foundCount`를 보라.
 * 뷰가 아직 없으면(`welcome` 전) `CONNECTING_INFO`다.
 */
export function buildMatchDebugInfo(view: ClientView | null, seed: string): SessionDebugInfo {
  if (view === null) return CONNECTING_INFO
  return {
    kind: 'match',
    phase: view.phase,
    seed,
    attempt: null,
    changeSeed: null,
    ammo: view.self.ammo,
    ammoMax: view.self.ammoMax,
    magazines: view.self.magazines,
    // 이번 라운드에 쓴 수는 뷰에 없다(호스트가 "몇 번 더 되는가"만 내린다). 지어내지 않는다.
    magazinesUsed: null,
    found: view.found.length,
    changes: view.changeCount,
    isInOriginRoom: isMeInOrigin(view),
    score: playerOf(view, view.me)?.roundScore ?? 0,
  }
}

/* --- 결과 --------------------------------------------------------------------------- */

/**
 * 순위표 한 줄. id → 이름은 여기서 찾는다(화면에 id가 나올 일은 없다).
 *
 * 내 구획 미발견(M11)은 **내 줄에만** 붙는다 - 그 수는 호스트가 본인에게만 내리는 값이고(`SelfView.unfoundCount`,
 * 리듀서 `unfoundOf`), 남의 것을 정답표에서 세면 점수의 분자가 화면에 두 벌째로 생긴다.
 */
function rankingRows(view: ClientView): readonly ResultRankingRow[] | undefined {
  if (view.ranking === null) return undefined
  return view.ranking.map((row) => ({
    name: nameOf(view, row.playerId),
    rank: row.rank,
    roundScore: row.roundScore,
    totalScore: row.totalScore,
    isMe: row.playerId === view.me,
    ...(row.playerId === view.me ? { zoneUnfound: view.self.unfoundCount } : {}),
  }))
}

/**
 * 결과 화면 뷰. 점수 내역(`breakdown`)은 싣지 않는다 - `computeScoreBreakdown`이 읽는 `maxCombo`·`hasPerfect`가
 * 뷰에 없고, 여기서 다시 세면 호스트가 굳힌 점수와 갈린다. 총점(`roundScore`)은 호스트가 굳힌 값 그대로다.
 * `endReason`은 `MatchEndReason`이 `RoundEndReason`의 부분집합이라 그대로 옮긴다(`all-out`은 여럿이의 것).
 *
 * @param huntElapsedMs 수색에 쓴 시간. 세션이 호스트 시각(`step.atMs`)의 차로 잰다.
 * @param missed        리빌 스텝에서 못 찾은 것(`sessionView.missedFrom`) - 라벨은 리빌과 같은 문자열이다.
 */
export function buildMatchResultView(
  view: ClientView,
  huntElapsedMs: number,
  missed: readonly MissedChangeView[],
): ResultView {
  const me = playerOf(view, view.me)
  const endReason: RoundEndReason = view.endReason ?? 'skipped'
  const ranking = rankingRows(view)
  /*
   * 내 구획의 머리글(M5 → M11). 찾는 사람의 얼굴("찾음 0/12") 옆에 "아무도 못 본 N개"가 함께 선다 - M11에서는
   * 전원이 자기 구획을 갖고 그 미발견이 곧 점수라, 이 줄이 없는 사람이 없다. 수는 호스트가 센 `self.unfoundCount`
   * (리듀서 `unfoundOf`)이고 리빌의 `missed`(방 전체)와 다르다 - 여기서 `changes`를 걸러 세면 점수의 분자가 두 벌이 된다.
   */
  const visitor = { unfoundCount: view.self.unfoundCount }
  return {
    seed: view.roomCode,
    // 여럿이에는 시도 번호가 없고 라운드가 있다. 머리 태그의 그 자리에 "N라운드"가 선다(`resultScreen.freeFace`).
    round: view.round,
    foundCount: me?.foundCount ?? 0,
    changeCount: view.changeCount,
    ammoLeft: view.self.ammo,
    huntElapsedMs,
    score: me?.roundScore ?? 0,
    endReason,
    missed,
    ...(ranking === undefined ? {} : { ranking }),
    // 선택 필드가 아니다 - 여럿이에는 언제나 내 구획이 있다(싱글만 이 줄 없이 결과를 만든다, `sessionResult`).
    visitor,
  }
}
